import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex, utf8ToBytes } from "@noble/hashes/utils.js";
import { finalizeEvent, type VerifiedEvent } from "nostr-tools/pure";

/**
 * Key proofs (PLAN.md 4.11 H1, 4.13 R2): the browser shows the server that it holds a key by
 * signing a server challenge. Each challenge names one operation and, where the operation has
 * content, a hash of that content, and the server accepts it once. A proof made to store a server
 * copy therefore cannot reset the PIN counter, store another copy or move a login.
 */
export const KIND_KEY_PROOF = 22242;

export const KEY_PROOF_OPS = ["register", "switch", "relink", "oprf", "backup", "unlocked", "admin-reset", "admin-role", "passkey", "revoke"] as const;
export type KeyProofOp = (typeof KEY_PROOF_OPS)[number];

export function isKeyProofOp(op: unknown): op is KeyProofOp {
  return typeof op === "string" && (KEY_PROOF_OPS as readonly string[]).includes(op);
}

/**
 * What a proof for `op` is bound to, as a hex SHA-256 over the JSON array of the parts (no two
 * different lists give the same text, whatever the parts contain); null for operations without
 * content. Browser and server compute it the same way:
 *   register          null (the new key signs it; it is its own content)
 *   switch            [new pubkey] (the current key agrees to this successor)
 *   relink, unlocked  null
 *   oprf              [blinded request]
 *   backup            [oprf key id, JSON of the copy]
 *   admin-reset       [member id, reason]
 *   admin-role        [member id, new role, reason] (the owner makes or removes an admin)
 *   passkey           [credential id, alg, public key] (signing in with this passkey)
 */
export function keyProofBinding(parts: readonly string[] | null): string | null {
  return parts === null ? null : bytesToHex(sha256(utf8ToBytes(JSON.stringify(parts))));
}

/** The signed kind 22242 event over a challenge. */
export function signKeyProof(challenge: string, secretKey: Uint8Array): VerifiedEvent {
  return finalizeEvent({ kind: KIND_KEY_PROOF, created_at: Math.floor(Date.now() / 1000), tags: [["challenge", challenge]], content: "" }, secretKey);
}
